import React, { useState, useEffect } from "react";
import { Phone, Mail, ArrowUpRight, Lock } from "lucide-react";
import logo from "./logo.jpg";
import settingsService from "./services/settingsService";
import servicesService from "./services/servicesService";

function Footer() {
  const [settings, setSettings] = useState({
    business_name: "VJ Dsign Studio",
    tagline: "Specializing in premium customized spaces, luxury interior structures, and curated modern decors tailored exclusively since 2017.",
    phone: "+91 87545 45419",
    phone_raw: "918754545419",
    email: "vjdsignstudio@gmail.com",
    instagram_url: "https://www.instagram.com/vjdsignstudio/",
    facebook_url: "https://www.facebook.com/share/16xZtiEpY5/",
  });

  const [activeServices, setActiveServices] = useState([]);

  useEffect(() => {
    async function loadFooterData() {
      try {
        const [general, all, services] = await Promise.all([
          settingsService.getSetting("general"),
          settingsService.getAll(),
          servicesService.getActive(),
        ]);

        if (general || all) {
          const rawPhone = (general?.phone || all?.phone || "").replace(/[^0-9]/g, "");
          setSettings((prev) => ({
            ...prev,
            business_name: general?.business_name || all?.business_name || prev.business_name,
            tagline: all?.footer_text || general?.tagline || all?.tagline || prev.tagline,
            phone: general?.phone || all?.phone || prev.phone,
            phone_raw: rawPhone || prev.phone_raw,
            email: general?.email || all?.email || prev.email,
            instagram_url: general?.instagram_url || all?.instagram_url || prev.instagram_url,
            facebook_url: general?.facebook_url || all?.facebook_url || prev.facebook_url,
          }));
        }

        if (services && services.length > 0) {
          setActiveServices(services.slice(0, 5));
        }
      } catch (err) {
        // Safe fallback
      }
    }
    loadFooterData();
  }, []);

  const quickLinks = [
    { name: "Home", link: "#home" },
    { name: "About Us", link: "#about-us" },
    { name: "Our Services", link: "#services" },
    { name: "Featured Projects", link: "#projects" },
    { name: "Pricing Packages", link: "#packages" },
    { name: "Studio Gallery", link: "#gallery" },
    { name: "Client Reviews", link: "#testimonials" },
    { name: "Contact Studio", link: "#contact" },
  ];

  const socialLinks = [
    { 
      icon: (
        <svg 
          className="w-[18px] h-[18px] fill-none stroke-current" 
          viewBox="0 0 24 24" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37zM17.5 6.5h.01"/>
        </svg>
      ), 
      url: settings.instagram_url, 
      name: "Instagram" 
    },
    { 
      icon: (
        <svg 
          className="w-[18px] h-[18px] fill-none stroke-current" 
          viewBox="0 0 24 24" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
        </svg>
      ), 
      url: settings.facebook_url, 
      name: "Facebook" 
    },
  ];

  return (
    <footer className="bg-[#9BB7A3] text-slate-800 font-semibold text-sm pt-16 border-t border-[#8aa892]/30">
      <div className="max-w-full lg:max-w-[92%] mx-auto px-6 lg:px-4 pb-12">
        
        {/* Main Footer Grid Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 lg:gap-12 text-left">
          
          {/* Column 1: Brand Info Box */}
          <div className="space-y-5">
            <div className="flex items-center gap-3 group cursor-pointer">
              <img 
                src={logo} 
                alt={settings.business_name} 
                className="w-16 h-16 object-contain transition-transform duration-500 group-hover:scale-105" 
              />
              <h2 className="text-base font-extrabold tracking-[1px] text-slate-900">
                {settings.business_name}
              </h2>
            </div>
            <p className="text-xs text-slate-700/90 leading-relaxed max-w-xs font-medium">
              {settings.tagline}
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-3 pt-2">
              {socialLinks.map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="w-9 h-9 rounded-xl bg-white/60 border border-white/40 text-slate-800 flex items-center justify-center transition-all duration-300 hover:bg-slate-900 hover:text-white hover:-translate-y-1 shadow-sm"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Navigation Links */}
          <div>
            <h3 className="text-slate-900 font-extrabold text-sm uppercase tracking-wider mb-5">
              Quick Links
            </h3>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2.5 font-semibold text-xs text-slate-700/90">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.link}
                    className="hover:text-white transition-colors duration-200 flex items-center gap-1 group truncate"
                  >
                    <span className="w-1 h-1 rounded-full bg-slate-700 group-hover:bg-white transition-colors" />
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Studio Services */}
          <div>
            <h3 className="text-slate-900 font-extrabold text-sm uppercase tracking-wider mb-5">
              Our Core Services
            </h3>
            <ul className="space-y-3 font-semibold text-xs text-slate-700/90">
              {activeServices.length > 0 ? (
                activeServices.map((service, i) => (
                  <li key={i}>
                    <a
                      href="#services"
                      className="hover:text-white transition-colors duration-200 flex items-center gap-1.5 group"
                    >
                      <ArrowUpRight size={14} className="text-slate-700 group-hover:text-white transition-colors" />
                      <span>{service.name}</span>
                    </a>
                  </li>
                ))
              ) : (
                <>
                  <li><a href="#services" className="hover:text-white">Residential Interiors</a></li>
                  <li><a href="#services" className="hover:text-white">Commercial Architecture</a></li>
                  <li><a href="#services" className="hover:text-white">Turnkey Renovation</a></li>
                </>
              )}
            </ul>
          </div>

          {/* Column 4: Contact Details */}
          <div>
            <h3 className="text-slate-900 font-extrabold text-sm uppercase tracking-wider mb-5">
              Reach Us Directly
            </h3>
            <ul className="space-y-4 font-semibold text-xs text-slate-700/90">
              <li>
                <a
                  href={`tel:${settings.phone_raw || "918754545419"}`}
                  className="flex items-center gap-3 hover:text-white transition-colors duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/60 border border-white/40 text-slate-900 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Phone size={14} />
                  </div>
                  <div className="text-left">
                    <span className="block text-[10px] uppercase tracking-wider text-slate-700 font-extrabold">Call Anytime</span>
                    <span className="font-bold text-sm text-slate-900 group-hover:text-white transition-colors">{settings.phone}</span>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${settings.email || "vjdsignstudio@gmail.com"}`}
                  className="flex items-start gap-3 hover:text-white transition-colors duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/60 border border-white/40 text-slate-900 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Mail size={14} />
                  </div>
                  <div className="text-left min-w-0">
                    <span className="block text-[10px] uppercase tracking-wider text-slate-700 font-extrabold">Write to Us</span>
                    <span className="font-bold text-sm text-slate-900 group-hover:text-white transition-colors truncate block">{settings.email}</span>
                  </div>
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Divider Border Line */}
        <div className="border-t border-slate-900/10 my-10" />

        {/* Bottom Copyright Content Panel */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left text-xs text-slate-700 font-medium">
          <p>© {new Date().getFullYear()} {settings.business_name}. All Rights Reserved.</p>
          <div className="flex items-center gap-4">
            <a
              href="/admin"
              className="inline-flex items-center gap-1 text-[11px] text-slate-800/80 hover:text-slate-950 font-bold bg-white/40 hover:bg-white/70 px-2.5 py-1 rounded-full transition shadow-sm"
            >
              <Lock size={11} /> Admin CMS
            </a>
            <p className="tracking-wide">
              Designed with Purpose by <span className="text-slate-900 font-bold">{settings.business_name}</span>
            </p>
          </div>
        </div>

      </div>
    </footer>
  );
}

export default Footer;